/**
 * Adapter Tela 7 · aba Métricas (T7-5) — `ConflictAlertMetricsDailyRaw[]` (N dias)
 * → `ConflictAlertMetricsVM`. O backend dá agregados POR DIA; aqui somamos/derivamos.
 *
 * Função pura. Placeholders honestos onde o backend não fornece (padrão Item 317):
 *   - reincidence.total/chainGt1/chain2/chain3plus: backend só dá `rate` → '—'.
 *   - avgMitigationTime: só `value` (min), sem target/SLA → sem ✓/⚠ na UI.
 *   - topOperators: sem fonte agregada → null (Item 321; placeholder na UI).
 *
 * R15 triangulado contra:
 *   - features/supervisor/types.ts (ConflictAlertMetricsDailyRaw)
 *   - features/supervisor/view-models.ts (ConflictAlertMetricsVM)
 *   - apps/engine/src/modules/adversarial/conflict-alerts/ (metrics controller)
 */
import { CATEGORY_LABELS } from './tela7-adapter';

import type { ConflictAlertMetricsDailyRaw } from '../../types';
import type { ConflictAlertMetricsVM, ConflictSeverityVM } from '../../view-models';

const PLACEHOLDER = '—';
const SEVERITIES: ConflictSeverityVM[] = ['critical', 'high', 'medium', 'low'];
const MAX_SEVERITY_DAYS = 12;

function sum(
  daily: ConflictAlertMetricsDailyRaw[],
  pick: (d: ConflictAlertMetricsDailyRaw) => number,
): number {
  return daily.reduce((acc, d) => acc + pick(d), 0);
}

/**
 * Agrega `topTypes` ({type,count}[] por dia) somando count por type entre todos os
 * dias; pct = count/total*100 (arredondado); label = CATEGORY_LABELS[type] ?? type.
 * Ordena desc por count.
 */
function aggregateTopTypes(
  daily: ConflictAlertMetricsDailyRaw[],
): ConflictAlertMetricsVM['topTypes'] {
  const byType = new Map<string, number>();
  for (const d of daily) {
    for (const t of d.topTypes) {
      byType.set(t.type, (byType.get(t.type) ?? 0) + t.count);
    }
  }
  const total = [...byType.values()].reduce((acc, n) => acc + n, 0);
  return [...byType.entries()]
    .map(([id, count]) => ({
      id,
      label: CATEGORY_LABELS[id] ?? id,
      count,
      pct: total > 0 ? Math.round((count / total) * 100) : 0,
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Tempo médio de mitigação por severidade: média dos dias QUE TÊM valor para a
 * severidade (dias sem valor não diluem). Severidades sem nenhum dia ficam ausentes.
 */
function aggregateAvgMitigationTime(
  daily: ConflictAlertMetricsDailyRaw[],
): ConflictAlertMetricsVM['avgMitigationTime'] {
  const out: ConflictAlertMetricsVM['avgMitigationTime'] = {};
  for (const sev of SEVERITIES) {
    const vals = daily
      .map((d) => d.avgMitigationTimeMinutesBySeverity[sev])
      .filter((v): v is number => v != null);
    if (vals.length > 0) {
      const avg = vals.reduce((acc, v) => acc + v, 0) / vals.length;
      out[sev] = { value: Math.round(avg), unit: 'min' };
    }
  }
  return out;
}

export function adaptMetrics(
  daily: ConflictAlertMetricsDailyRaw[],
  activeCount: number,
): ConflictAlertMetricsVM {
  const days = daily.length;

  // MÉDIA DIÁRIA das taxas (não a taxa ponderada do período): o backend só expõe o
  // `reincidenceRate` por dia, não os counts (reincidentes/total) para ponderar. A UI
  // rotula honestamente "média diária". Taxa ponderada real depende de campo no backend
  // (item de backend registrado na revisão T7).
  const rates = daily.map((d) => Number(d.reincidenceRate)).filter((n) => !Number.isNaN(n));
  const avgRate = rates.length > 0 ? rates.reduce((acc, n) => acc + n, 0) / rates.length : 0;

  const severityOverTime = daily.slice(-MAX_SEVERITY_DAYS).map((d) => ({
    critical: d.severityDistribution.critical ?? 0,
    high: d.severityDistribution.high ?? 0,
    medium: d.severityDistribution.medium ?? 0,
    low: d.severityDistribution.low ?? 0,
  }));

  return {
    period: `últimos ${days} dias`,
    days,
    totals: {
      active: activeCount,
      detected: sum(daily, (d) => d.totalDetected),
      mitigated: sum(daily, (d) => d.totalMitigated),
      accepted: sum(daily, (d) => d.totalAccepted),
      falsePositive: sum(daily, (d) => d.totalFalsePositive),
      resolved: sum(daily, (d) => d.totalResolved),
      revoked: sum(daily, (d) => d.totalRevoked),
    },
    avgMitigationTime: aggregateAvgMitigationTime(daily),
    topTypes: aggregateTopTypes(daily),
    reincidence: {
      rate: Math.round(avgRate * 100),
      // Backend só fornece `rate`, não os contadores de chain → honesto.
      total: PLACEHOLDER,
      chainGt1: PLACEHOLDER,
      chain2: PLACEHOLDER,
      chain3plus: PLACEHOLDER,
    },
    severityOverTime,
    topOperators: null,
  };
}
